import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

export interface SidePanelSkeletonProps {
  /**
   * 面板宽度类名
   * @example 'w-62'（248px，素材库/文案库文件夹树）、'w-45'（180px，资产管理导航卡）
   */
  widthClassName?: string;
  /**
   * 是否绘制顶部 h-12 标题行
   * @default true
   */
  withHeader?: boolean;
  /** 条目占位数量 */
  itemCount?: number;
  /** 单条占位高度：树节点约 32px、导航/分类按钮约 36px */
  itemHeight?: number;
  className?: string;
}

/**
 * 左侧面板骨架：顶部标题行 + 一列条目占位。
 *
 * 覆盖本仓库「左栏 + 右主区」页面里左栏的三种真实形态：
 * - 文件夹树：`material-library` / `text-library` 的 `FolderTree`（`w-62` + h-12 标题 + 树节点）
 * - 导航卡：`resource-management` 的 `Card w-45`（无标题 + 竖排按钮）
 * - 分类卡：`label-management` 的 `Card flex-1 min-w-45`（h-12 标题 + 分类列表）
 *
 * 与 `ListPageSkeleton` 一样：只允许依赖 `@/components/ui/*` 与 `cn`，禁止引用页面模块。
 */
export function SidePanelSkeleton({
  widthClassName = 'w-62',
  withHeader = true,
  itemCount = 9,
  itemHeight = 32,
  className,
}: SidePanelSkeletonProps) {
  return (
    <div
      data-testid="side-panel-skeleton"
      className={cn(
        'flex h-full shrink-0 flex-col overflow-hidden rounded-[16px] border bg-card',
        widthClassName,
        className
      )}
    >
      {withHeader && (
        <div className="flex h-12 shrink-0 items-center px-4">
          <Skeleton className="h-5 w-24" />
        </div>
      )}
      <div className="flex min-h-0 flex-1 flex-col gap-y-1 overflow-hidden px-2 pb-2">
        {Array.from({ length: itemCount }, (_, index) => (
          <Skeleton key={index} className="w-full shrink-0" style={{ height: itemHeight }} />
        ))}
      </div>
    </div>
  );
}
